import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client'

import { useAiChat } from '@factorialco/f0-react'
import {
  CreateOneAnalyticsDashboardDocument,
  CreateOneAnalyticsDashboardMutation,
  FetchSavedDashboardByIdDocument,
  FetchSavedDashboardDocument,
  OneAnalyticsDashboardManagementPolicyDocument,
  OneAnalyticsDashboardCategoryEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { useReportDashboardsFeature } from 'modules/ai/hooks/useReportDashboardsFeature'

import { defaultDashboardSpecs } from 'modules/ai/dashboardSpecs'

import { trackOneAnalyticsSavedExplorationOpened } from './telemetry'
import type { OneAnalyticsSavedExplorationOpenSource } from './telemetry'

type UseOpenDashboardActionResult = {
  openDashboard?: (category: OneAnalyticsDashboardCategoryEnum) => Promise<void>
  openDashboardById?: (dashboardId: string) => Promise<void>
  openChatForNewDashboard: () => void
  sendPromptForNewDashboard: (prompt: string) => void
}

type UseOpenDashboardActionOptions = {
  persistedDashboardActions?: boolean
}

const buildOpenDashboardGreeting = (title: string): string =>
  [
    i18n.t('ai_reports.dashboards.chat.open_greeting.heading', { title }),
    '',
    i18n.t('ai_reports.dashboards.chat.open_greeting.options_intro'),
    '',
    `- ${i18n.t('ai_reports.dashboards.chat.open_greeting.add_widgets')}`,
    `- ${i18n.t('ai_reports.dashboards.chat.open_greeting.explain_insights')}`,
    `- ${i18n.t('ai_reports.dashboards.chat.open_greeting.filter_view')}`,
    `- ${i18n.t('ai_reports.dashboards.chat.open_greeting.refine_chart')}`,
  ].join('\n')

const buildNewDashboardGreeting = (): string =>
  [
    i18n.t('ai_reports.dashboards.chat.create_greeting.heading'),
    '',
    i18n.t('ai_reports.dashboards.chat.create_greeting.options_intro'),
    '',
    `- ${i18n.t('ai_reports.dashboards.chat.create_greeting.start_from_question')}`,
    `- ${i18n.t('ai_reports.dashboards.chat.create_greeting.track_kpi')}`,
    `- ${i18n.t('ai_reports.dashboards.chat.create_greeting.slice_by_dimension')}`,
    `- ${i18n.t('ai_reports.dashboards.chat.create_greeting.explore_idea')}`,
  ].join('\n')

const DEFAULT_TITLES: Record<OneAnalyticsDashboardCategoryEnum, () => string> = {
  trainings: () => i18n.t('common.ai.default_dashboard_title.trainings'),
  recruitment: () => i18n.t('common.ai.default_dashboard_title.recruitment'),
  communities: () => i18n.t('common.ai.default_dashboard_title.communities'),
  analytics: () => '',
}

const EMPTY_SPECS = JSON.stringify({ items: [], fetchSpecs: {}, filters: null })

const getDefaultSpecs = (category: OneAnalyticsDashboardCategoryEnum): string => {
  const spec = defaultDashboardSpecs[category]
  return spec ? JSON.stringify(spec) : EMPTY_SPECS
}

type ParsedSpecs = {
  items: unknown
  fetchSpecs: unknown
  filters?: unknown
  navigationFilters?: unknown
}

const buildDashboardConfig = (title: string, specsJson: string, description = '') => {
  const specs = parseSpecs(specsJson)

  return {
    title,
    description,
    items: specs.items,
    fetchSpecs: specs.fetchSpecs,
    filters: specs.filters ?? undefined,
    navigationFilters: specs.navigationFilters ?? undefined,
  }
}

/**
 * Parse persisted specs defensively. Saved dashboards can outlive schema
 * changes, so malformed / partial JSON shouldn't crash the page. Falls
 * back to `EMPTY_SPECS` and lets the agent rebuild the dashboard from
 * the seeded context instead.
 */
const parseSpecs = (specsJson: string): ParsedSpecs => {
  try {
    return JSON.parse(specsJson)
  } catch {
    return JSON.parse(EMPTY_SPECS)
  }
}

/**
 * Shared hook that opens a saved dashboard in the One AI chat.
 *
 * Mounted once by the header (`FactorialOnePage`) so pages don't need to
 * know anything about dashboards — the header's open-dashboard action
 * calls `openDashboard(category)` directly when the user clicks.
 *
 * The dashboard fetch itself is lazy: no dashboard payload hits the network
 * until a user actually clicks the button. Persisted-dashboard actions opt
 * into policy queries so consumers that only open the chat do not add policy
 * requests to every page render.
 */
export const useOpenDashboardAction = ({
  persistedDashboardActions = false,
}: UseOpenDashboardActionOptions = {}): UseOpenDashboardActionResult => {
  const { company, employee } = useCurrentSession()
  const apolloClient = useApolloClient()
  const { clear, setOpen, appendMessages, setPendingContext, sendMessage } = useAiChat()
  const { enabled: reportDashboardsEnabled } = useReportDashboardsFeature({
    skip: !persistedDashboardActions,
  })
  const { data: managementPolicyData } = useQuery(OneAnalyticsDashboardManagementPolicyDocument, {
    skip: !persistedDashboardActions || !reportDashboardsEnabled || !employee,
  })
  const canManageDashboards = Boolean(
    managementPolicyData?.oneAnalytics?._policies?.manageDashboards?.can
  )

  const [createDashboard] = useMutation(CreateOneAnalyticsDashboardDocument, {
    extractorFn: (result: CreateOneAnalyticsDashboardMutation) =>
      result.oneAnalyticsMutations.createOneanalyticsDashboard,
    refetchQueries: [FetchSavedDashboardDocument],
  })

  const openDashboardWithContext = useCallback(
    (
      title: string,
      specsJson: string,
      savedId?: string,
      savedCategory?: OneAnalyticsDashboardCategoryEnum,
      savedDescription?: string,
      openedFrom?: OneAnalyticsSavedExplorationOpenSource
    ) => {
      const config = buildDashboardConfig(title, specsJson, savedDescription)

      if (savedId && savedCategory && openedFrom) {
        trackOneAnalyticsSavedExplorationOpened({
          config,
          dashboardId: savedId,
          category: savedCategory,
          openedFrom,
        })
      }

      // Start a fresh chat
      clear()
      setOpen(true)

      // Store context for the agent (sent as text part on first message).
      // Include saved metadata so the agent returns them on iteration.
      setPendingContext({
        label: title,
        context: JSON.stringify({
          ...config,
          savedDashboardId: savedId,
          savedDashboardCategory: savedCategory,
          savedDashboardDescription: savedDescription,
        }),
      })

      // Inject a client-only assistant message so the dashboard card
      // renders in the chat. The DashboardCard auto-opens the canvas
      // via useAutoOpenCanvas, so no manual openCanvas needed.
      setTimeout(() => {
        appendMessages(
          [
            {
              role: 'assistant',
              content: buildOpenDashboardGreeting(title),
              toolCalls: [
                {
                  id: crypto.randomUUID(),
                  function: {
                    name: 'displayDashboard',
                    arguments: JSON.stringify({
                      ...config,
                      savedDashboardId: savedId,
                      savedDashboardCategory: savedCategory,
                      savedDashboardDescription: savedDescription,
                      savedDashboardUnsaved: false,
                    }),
                  },
                },
              ],
            },
          ],
          { persist: false }
        )
      }, 0)
    },
    [clear, setOpen, setPendingContext, appendMessages]
  )

  const openDashboard = useCallback(
    async (category: OneAnalyticsDashboardCategoryEnum) => {
      if (!reportDashboardsEnabled || !canManageDashboards) return

      try {
        const { data } = await apolloClient.query({
          query: FetchSavedDashboardDocument,
          fetchPolicy: 'network-only',
          variables: {
            category,
            companyIds: [company.id],
          },
        })
        const dashboard = data?.oneAnalytics.dashboardsConnection.nodes[0]

        if (dashboard) {
          openDashboardWithContext(
            dashboard.title,
            dashboard.specs,
            dashboard.id,
            category,
            dashboard.description ?? undefined,
            'one_dashboard_action'
          )
          return
        }

        if (!employee) return

        const title = DEFAULT_TITLES[category]()
        const specs = getDefaultSpecs(category)
        const response = await createDashboard({
          variables: {
            title,
            category,
            specs,
            companyId: company.id,
          },
        })
        const handled = handleResult<{ dashboard?: { id: string } | null }>(
          response,
          () => undefined
        )
        if (!handled.success) {
          // handleResult already surfaces the server error; bail out
          // so we don't open a dashboard that wasn't actually persisted.
          return
        }
        // Propagate the created dashboard's id so the injected
        // displayDashboard toolCall is marked as `savedDashboardId`,
        // enabling subsequent save/update flows.
        openDashboardWithContext(title, specs, handled.data.dashboard?.id, category, undefined)
      } catch {
        app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
      }
    },
    [
      canManageDashboards,
      reportDashboardsEnabled,
      apolloClient,
      company.id,
      employee,
      openDashboardWithContext,
      createDashboard,
    ]
  )

  const openDashboardById = useCallback(
    async (dashboardId: string) => {
      if (!reportDashboardsEnabled || !canManageDashboards) return
      try {
        const { data } = await apolloClient.query({
          query: FetchSavedDashboardByIdDocument,
          fetchPolicy: 'network-only',
          variables: { dashboardId },
        })
        const dashboard = data?.oneAnalytics.dashboard

        if (!dashboard) {
          app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
          return
        }

        openDashboardWithContext(
          dashboard.title,
          dashboard.specs,
          dashboard.id,
          dashboard.category,
          dashboard.description ?? undefined,
          'dashboards_list'
        )
      } catch {
        app.setFlash({ type: 'wrong', message: i18n.t('common.error') })
      }
    },
    [reportDashboardsEnabled, canManageDashboards, apolloClient, openDashboardWithContext]
  )

  const openChatForNewDashboard = useCallback(() => {
    clear()
    setOpen(true)

    setPendingContext({
      label: i18n.t('ai_reports.dashboards.chat.new_report_context_label'),
      context: i18n.t('ai_reports.dashboards.routing_instruction'),
    })

    setTimeout(() => {
      appendMessages(
        [
          {
            role: 'assistant',
            content: buildNewDashboardGreeting(),
          },
        ],
        { persist: false }
      )
    }, 0)
  }, [clear, setOpen, setPendingContext, appendMessages])

  /**
   * Send a predefined prompt to the AI chat as if the user had typed it.
   * No pending-context is attached — the prompt itself is the user message
   * and routing is left to the agent based on the message contents.
   */
  const sendPromptForNewDashboard = useCallback(
    (prompt: string) => {
      setOpen(true)
      sendMessage(prompt)
    },
    [setOpen, sendMessage]
  )

  return {
    openDashboard:
      persistedDashboardActions && reportDashboardsEnabled && canManageDashboards
        ? openDashboard
        : undefined,
    openDashboardById:
      persistedDashboardActions && reportDashboardsEnabled && canManageDashboards
        ? openDashboardById
        : undefined,
    openChatForNewDashboard,
    sendPromptForNewDashboard,
  }
}
